import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/currency";
import { Star, Heart, ChevronLeft, ChevronRight, TrendingUp } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function TopTours() {
  const [tours, setTours] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    api.get("/listings/top", { params: { limit: 6 } })
      .then((r) => setTours(r.data))
      .catch(() => {});
  }, []);

  const scroll = (dir) => {
    if (scroller.current) scroller.current.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  if (tours.length === 0) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-16 md:pt-24 relative z-10" data-testid="top-tours">
      <div className="flex items-end justify-between mb-6">
        <div>
          <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-orange-400 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Traveler favorites
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Top Tours</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => scroll(-1)} data-testid="top-tours-prev" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 hover:text-orange-400"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => scroll(1)} data-testid="top-tours-next" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 hover:text-orange-400"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>

      <div ref={scroller} className="flex gap-5 overflow-x-auto no-scrollbar pb-2 snap-x snap-mandatory">
        {tours.map((t) => {
          const img = t.image_url || t.images?.[0];
          return (
            <Link
              key={t.id}
              to={`/listing/${t.id}`}
              data-testid={`top-tour-${t.id}`}
              className="group relative shrink-0 w-[320px] snap-start rounded-2xl overflow-hidden glass glass-hover"
            >
              <div className="relative aspect-[16/11] overflow-hidden bg-[#0B0E1B]">
                <img src={img ? fileUrl(img) : FALLBACK} alt={t.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070A18] via-[#070A18]/20 to-transparent" />
                <span className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full bg-[#FF2D6E] text-white shadow-lg" data-testid="most-loved-badge">
                  <Heart className="w-3 h-3 fill-white" /> Most Loved
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-heading text-lg font-bold text-white leading-tight line-clamp-2 group-hover:text-orange-400 transition-colors">{t.title}</h3>
                <p className="text-xs text-slate-400 mt-1 truncate">{t.island}{t.category ? ` · ${t.category}` : ""}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="flex items-center gap-1.5 text-emerald-400 font-semibold text-sm" data-testid="top-tour-rating">
                    <Star className="w-4 h-4 fill-emerald-400" /> {Number(t.star_rating || 0).toFixed(1)}
                    <span className="text-slate-500 font-normal">({t.review_count})</span>
                  </span>
                  <span className="text-[#FF3D57] font-heading font-extrabold text-lg" data-testid="top-tour-price">
                    {formatPrice(t.price)}
                    <span className="text-xs text-slate-500 font-normal"> / {t.price_type === "per_group" ? "group" : "person"}</span>
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
